import { useCallback, useEffect, useMemo } from 'react'

import * as Sentry from '@sentry/nextjs'

import { useDevicesCameraQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

import { CLOUD_STREAMING_TYPES } from '../constants/constants'
import { CloudCameraOptionIProps } from '../types/types'
import { mapCloudCameras } from '../utils/mapCloudCameras'

interface UseCloudCamerasResultIProps {
  cameras: CloudCameraOptionIProps[]
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Fetches the cloud cameras (Verkada / Ava / Avigilon) available to place on the
 * video wall. Called once at the VideoWall level and passed down to each empty
 * cell's picker (rather than fetching per-cell).
 *
 * Exposes `isError` + `refetch` so the UI can tell a genuine "no cameras" result
 * apart from a failed fetch. Without this, a query error leaves `cameras` empty
 * and the wall looks like the account simply has no cameras.
 */
export const useCloudCameras = (): UseCloudCamerasResultIProps => {
  const {
    data,
    loading: isLoading,
    error,
    refetch,
  } = useDevicesCameraQuery({
    variables: {
      filter: {
        type: DeviceType.Camera,
        streamingTypeIn: CLOUD_STREAMING_TYPES,
      },
    },
    // Surface refetch progress as `isLoading` so the Retry action shows feedback.
    notifyOnNetworkStatusChange: true,
  })

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: { feature: 'video_wall', action: 'fetch_cloud_cameras' },
      })
    }
  }, [error])

  const cameras = useMemo(() => mapCloudCameras(data), [data])

  // Apollo's refetch rejects on failure; that rejection is already surfaced via
  // `error` above, so swallow it here to avoid an unhandled promise rejection.
  const refetchCameras = useCallback(() => {
    refetch().catch(() => undefined)
  }, [refetch])

  return {
    cameras,
    isLoading,
    isError: Boolean(error),
    refetch: refetchCameras,
  }
}
